import axios from "axios";

import { apiConfig } from "../config/authConfig";

import { IBatch, IBatchHistory, IBatchHistoryAddRequest, IBatchLight, IBatchSearchRequest, IBatchShipment, IBatchShipmentOperationRequest, IBatchShipmentSearchRequest, IBatchStatusChangeRequest } from "../types/shipment";

import { CredentialManager } from "../classes/credentialManager";
import { CustomerService } from "./customerService";


export namespace BatchService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Convert one api response object to one Batch.
	 * @param data Batch response data from an API.
	 **********/
	export function mapBatch(data: any) {
		let batch: IBatch = {
			id: data.id,
			invoiceNumber: data.invoiceNumber,
			month: data.month,
			payeeId: data.payeeId,
			payeeType: data.payeeType,
			payeeName: data.payeeName,
			status: data.status,
			shipments: mapBatchShipments(data.shipments),
			customers: CustomerService.mapCustomerLightList(data.customers)
		}

		return batch;
	}

	/**********
	 * Convert an array of api response objects to an array of Batches.
	 * @param data Batch data from an API response.
	 **********/
	export function mapBatches(data: any) {
		let batches: IBatch[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				batches.push(mapBatch(item));
			})
		}

		return batches;
	}

	/**********
	 * Convert one api response object to one BatchLight.
	 * @param data BatchLight response data from an API.
	 **********/
	export function mapBatchLight(data: any) {
		let batch: IBatchLight = {
			id: data.id,
			invoiceNumber: data.invoiceNumber,
			month: data.month,
			payeeId: data.payeeId,
			payeeType: data.payeeType,
			status: data.status
		}

		return batch;
	}

	/**********
	 * Convert an array of api response objects to an array of BatchLights.
	 * @param data BatchLight data from an API response.
	 **********/
	export function mapBatchLights(data: any) {
		let batches: IBatchLight[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				batches.push(mapBatchLight(item));
			})
		}

		return batches;
	}

	/**********
	 * Convert one api response object to one BatchShipemnt.
	 * @param data BatchShipment response data from an API.
	 **********/
	export function mapBatchShipment(data: any) {
		let batchShipment: IBatchShipment = {
			id: data.id,
			batchID: data.batchID,
			shipmentId: data.shipmentId,
			bolNumber: data.bolNumber
		}

		return batchShipment;
	}

	/**********
	 * Convert an array of api response objects to an array of BatchShipments.
	 * @param data BatchShipment data from an API response.
	 **********/
	export function mapBatchShipments(data: any) {
		let batchShipments: IBatchShipment[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				batchShipments.push(mapBatchShipment(item));
			})
		}

		return batchShipments;
	}

	/**********
	 * Gets payees.
	 **********/
	export async function getPayees() {
		const response = axios.get(
			apiConfig.default + "/batch/payees",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets months of existing batches.
	 **********/
	export async function getExistingMonths() {
		const response = axios.get(
			apiConfig.default + "/batch/months",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets the status of a single batch by its id.
	 * @param batchId the batches Id.
	 **********/
	export async function getStatus(batchId: string) {
		const response = axios.get(
			apiConfig.default + `/batch/${batchId}/status`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets the status name of a single batch by its id.
	 * @param batchId the batches Id.
	 **********/
	export async function getStatusName(batchId: string) {
		const response = axios.get(
			apiConfig.default + `/batch/${batchId}/status/name`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets documents associated with a batch.
	 * @param batchId The id of the batch.
	 **********/
	export async function getBatchDocuments(batchId: string) {
		const apiResponse = axios.get(
			apiConfig.default + "/batch/" + batchId + "/documents",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Add a batch for grouping shipments.
	 * @param batch Information about the batch.
	 **********/
	export async function addBatch(batch: IBatch) {
		const apiResponse = axios.post(
			apiConfig.default + "/batch",
			batch,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Delete a batch of grouped shipments.
	 * @param id The ID of the batch.
	 **********/
	export async function deleteBatch(id: number) {
		const apiResponse = axios.delete(
			apiConfig.default + "/batch/" + id,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Sets batch status.
	 * @param request Information about the new status.
	 **********/
	export async function setStatus(request: IBatchStatusChangeRequest) {
		const apiResponse = axios.put(
			apiConfig.default + `/batch/${request.id}/status`, 
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update batch details.
	 * @param batch Batch details.
	 **********/
	export async function setDetails(batch: IBatch) {
		const apiResponse = axios.put(
			apiConfig.default + "/batch",
			batch,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets a batch by its ID.
	 * @param id The ID of the batch.
	 **********/
	export async function getBatch(id: string) {
		const apiResponse = axios.get(
			apiConfig.default + "/batch/" + id,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets an abbreviated list of all batches.
	 * @param includeComplete Whether to include batches with a status of Complete.
	 **********/
	export async function getBatchLights(includeComplete: boolean = false) {
		const apiResponse = axios.get(
			apiConfig.default + "/batch/light/" + (includeComplete ? "all" : "active"),
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Search for batches.
	 * @param request The search request.	 
	 **********/
	export async function searchBatches(request: IBatchSearchRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/batch/search",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Add shipments to a batch via their IDs.
	 * @param batchId The ID of the batch.
	 * @param shipmentIds The IDs of the shipments being added to the batch.
	 **********/
	export async function addBatchShipments(batchId: string, shipmentIds: number[]) {
		const request: IBatchShipmentOperationRequest = { shipmentIds: shipmentIds };
		const apiResponse = axios.post(
			apiConfig.default + "/batch/" + batchId + "/shipments/add",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Remove shipments from a batch via their IDs.
	 * @param batchId The ID of the batch.
	 * @param shipmentIds The IDs of the shipments being removed from the batch.
	 **********/
	export async function removeBatchShipments(batchId: string, shipmentIds: number[]) {
		const request: IBatchShipmentOperationRequest = { shipmentIds: shipmentIds };
		const apiResponse = axios.post(
			apiConfig.default + "/batch/" + batchId + "/shipments/remove",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Get shipment associated to a batch.
	 * @param batchId The ID of the batch.
	 **********/
	export async function getBatchShipments(batchId: string) {
		const apiResponse = axios.get(
			apiConfig.default + "/batch/" + batchId + "/shipments",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Search for shipments to add to the batch.
	 * @param criteria The search criteria as a comma-separated list of BOL or invoice numbers.	 
	 **********/
	export async function searchBatchShipments(criteria: string) {		
		const request: IBatchShipmentSearchRequest = { criteria: criteria };
		const apiResponse = axios.post(
			apiConfig.default + "/batch/shipments/search",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Convert one api response object to one Batch history comment.
	 * @param data BatchHistory response data from an API.
	 **********/
	export function mapBatchHistory(data: any): IBatchHistory {
		let history: IBatchHistory = {
			id: data.id,
			batchId: data.batchId,
			type: data.type,
			category: data.category,
			action: data.action,
			username: data.username,
			description: data.description,
			date: new Date(data.date),
			oldStatus: data.oldStatus,
			newStatus: data.newStatus,
			misc: data.misc
		};

		return history;
	}

	/**********
	 * Convert an array of api response objects to an array of Batch history comments.
	 * @param data BatchHistory data from an API response.
	 **********/
	export function mapBatchHistories(data: any): IBatchHistory[] {
		let comments: IBatchHistory[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				comments.push(mapBatchHistory(item));
			})
		}

		return comments;
	}

	/**********
	 * Adds a comment to the batch's change history.
	 * @param request The comment to add.
	 **********/
	export async function addHistoryComment(id: string, request: IBatchHistoryAddRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/batch/" + id + "/history",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets comments from a batch's change history.
	 * @param id The batch id.
	 **********/
	export async function getBatchHistory(id: string) {
		const response = axios.get(
			apiConfig.default + "/batch/" + id + "/history",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets a set of manifest warnings for each of the batch's shipments that has manifest irregularities keeping it from progressing in the workflow.
	 * @param id The batch id.
	 **********/
	export async function getShipmentManifestWarningSetsByBatchId(id: number) {
		const response = axios.get(
			apiConfig.default + "/batch/" + id + "/manifest/warningsets",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}
}
